import { Services } from '@Shared.Angular/@types/services';
import angular from 'angular';
import {
  IExternalBusinessSummary,
  IExternalBusinessDetail,
  IExternalBusinessProviderInfo
} from '@Shared.Angular/@types/core/contracts/queryModel/externalBusinessProvider/externalBusinessProvider';

export default class ExternalBusinessLookupService {
  public static $inject = ['$http', '$q', 'APP_CONFIG'];

  private static readonly DEFAULT_SEARCH_LIMIT = 10;

  private readonly AUTHENTICATED_API: string;
  private readonly PUBLIC_FORM_API: string;

  constructor(
    private $http: angular.IHttpService,
    private $q: angular.IQService,
    APP_CONFIG: Services.APP_CONFIG
  ) {
    this.AUTHENTICATED_API = APP_CONFIG.apiBaseUrl + 'externalbusinesses';
    this.PUBLIC_FORM_API = APP_CONFIG.apiBaseUrl + 'form/externalbusinesses';
  }

  public getProviders(): angular.IPromise<IExternalBusinessProviderInfo[]> {
    return this.$http
      .get<IExternalBusinessProviderInfo[]>(
        `${this.AUTHENTICATED_API}/providers`
      )
      .then((response) => response.data)
      .catch((error) => {
        console.error('Failed to get business providers:', error);
        return [];
      });
  }

  public searchBusinesses(
    query: string,
    provider: number,
    limit: number = ExternalBusinessLookupService.DEFAULT_SEARCH_LIMIT,
    businessId?: string
  ): angular.IPromise<IExternalBusinessSummary[]> {
    if (!query || query.trim().length === 0) {
      return this.$q.resolve([]);
    }

    const isPublicForm = !!businessId;
    const url = isPublicForm ? this.PUBLIC_FORM_API : this.AUTHENTICATED_API;

    const params: Record<string, string | number> = {
      query: query.trim(),
      provider: provider,
      limit: limit
    };

    if (businessId) {
      params.businessId = businessId;
    }

    return this.$http
      .get<IExternalBusinessSummary[]>(url, { params })
      .then((response) => response.data)
      .catch((error) => {
        console.error('Business search failed:', error);
        return [];
      });
  }

  public getBusinessDetails(
    externalBusinessId: string,
    provider: number,
    formBusinessId?: string
  ): angular.IPromise<IExternalBusinessDetail> {
    if (!externalBusinessId || !externalBusinessId.trim()) {
      return this.$q.reject('Business ID is required');
    }

    const isPublicForm = !!formBusinessId;
    const url = isPublicForm ? this.PUBLIC_FORM_API : this.AUTHENTICATED_API;

    const params: Record<string, string | number> = {
      provider: provider
    };

    if (formBusinessId) {
      params.businessId = formBusinessId;
    }

    return this.$http
      .get<IExternalBusinessDetail>(
        `${url}/${encodeURIComponent(externalBusinessId.trim())}`,
        { params }
      )
      .then((response) => response.data)
      .catch((error) => {
        console.error('Failed to get business details:', error);
        throw error;
      });
  }

  public formatBusinessName(business: IExternalBusinessDetail): string {
    if (!business) return '';
    return business.entityName || '';
  }

  public isValidBusiness(value: { id?: string } | null): boolean {
    return !!value && !!value.id;
  }
}

angular
  .module('flowingly.services')
  .service('externalBusinessLookupService', ExternalBusinessLookupService);

export type ExternalBusinessLookupServiceType = InstanceType<
  typeof ExternalBusinessLookupService
>;
